import Tooltip from "@components/ui/tooltip";
import { IconAlertTriangle, IconInfoCircle } from "@tabler/icons-react";
import { memo } from "react";

type MetricItemProps = {
  metric: {
    label: string;
    value: string;
    change: number;
    tooltip?: string;
    error: string;
  };
  loading: boolean;
  error: boolean;
};

const formatChange = (change: number): string => {
  const sign = change >= 0 ? "+" : "";
  return `${sign}${change}%`;
};

const isPositive = (change: number): boolean => change >= 0;

export const MetricItem = memo(
  ({ metric, loading, error }: MetricItemProps) => {
    const positive = isPositive(metric.change);
    return (
      <div className="tw-flex tw-flex-col tw-gap-2 tw-py-2 tw-px-4 -lg:tw-px-2">
        {/* Label with optional info icon */}
        <div className="tw-flex tw-items-center tw-gap-1">
          <span className="tw-font-sans tw-text-xs tw-font-normal tw-text-blue">
            {metric.label}
          </span>
          {metric.tooltip && (
            <Tooltip>
              <Tooltip.Trigger>
                <IconInfoCircle className="tw-size-4 tw-text-blue-500" />
              </Tooltip.Trigger>
              <Tooltip.Content className="tw-z-[99999]">
                <Tooltip.Text className="tw-max-w-72 tw-text-center">
                  {metric.tooltip}
                </Tooltip.Text>
              </Tooltip.Content>
            </Tooltip>
          )}
        </div>

        {/* Value and change badge */}
        {loading ? (
          <div className="tw-h-7 tw-flex tw-items-center">
            <span className="tw-animate-skeleton-loading tw-block tw-h-4 tw-rounded-lg tw-bg-blue-100/50 tw-w-16"></span>
          </div>
        ) : (
          <div className="tw-flex tw-items-center tw-gap-3">
            <span className="tw-font-sans tw-text-xl tw-font-medium tw-text-blue">
              {error ? metric.error : metric.value}
            </span>

            {error ? (
              <div className="tw-size-6 tw-flex tw-items-center tw-justify-center tw-bg-[#FEF3C7] tw-rounded-full">
                <IconAlertTriangle className="tw-text-[#F59E0B] tw-size-3" />
              </div>
            ) : (
              metric.change !== 0 && (
                <span
                  className="tw-font-sans tw-text-xs tw-font-semibold tw-text-white tw-px-1 tw-py-0.5 tw-rounded-md"
                  style={{
                    backgroundColor: positive ? "#DCFCE7" : "#FEE2E2",
                    color: positive ? "#16A34A" : "#DC2626",
                  }}
                >
                  {formatChange(metric.change)}
                </span>
              )
            )}
          </div>
        )}
      </div>
    );
  },
);
